import isEmpty from 'lodash-es/isEmpty';
import { LATLONG } from '../../models/map-configuration.model';
import { AppFacadeService } from '../../services/app-facade.service';
import { NotificationService } from '../../services/notification.service';
import { OnInit, AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { Subscription, Observable, BehaviorSubject, combineLatest, tap, timer } from 'rxjs';
import filter from 'lodash-es/filter';
import size from 'lodash-es/size';
import { ConfigurationService } from '../../services/configuration.service';
import { TrackService } from '../../services/track.service';
import { ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../services/logger.service';
import { TrackingResponse } from '../../models/tracking-result.model';
import { AppMediatorService } from '../../services/app-mediator.service';
import { GoogleMapResult } from '../../models/google-map-result.model';
import { FeatureToggleService } from '../../services/feature-toggle.service';

@Component({
    standalone: false,
    selector: 'por-tracking',
    templateUrl: './tracking.component.html',
    styleUrls: ['./tracking.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrackingComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly notification = inject(NotificationService);
    private readonly configService = inject(ConfigurationService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly trackingService = inject(TrackService);
    private readonly translateService = inject(TranslateService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    private readonly featureToggleService = inject(FeatureToggleService);

    mapReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
    contract$: Observable<unknown> = new Observable();
    subscriptions: Subscription[] = [];
    isPickUpOrder = false;
    pushpins: LATLONG[] = [];
    customerId!: string;
    trackingDetails: Observable<TrackingResponse[]> = this.trackingService.trackingDetails.asObservable();
    noTrackingOrders$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    orderExistsWithNoAddress$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    inputs: ConsumerPortalConfig | undefined;
    @Input() config: string | undefined;
    @Input() width: string | undefined;
    @Input() height: string | undefined;

    @Input() mapHeight: string | undefined;
    @Output() readonly elementLoaded = new EventEmitter<boolean>();
    loaded$ = this.trackingService.loaded$.asObservable();

    isLiveLinkLoading = signal(false);
    liveLinkUrl = signal<string | null>(null);
    isLiveTrackingFeatureEnabled = this.featureToggleService.isAvailable('liveTracking') && this.featureToggleService.isAvailable('Account.UseHapnGps');
    private liveLinkInitialized = false;
    ngAfterViewInit(): void {
        this.cdRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.configService.setMatIconFontFamily();

        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config) as ConsumerPortalConfig;
            this.logger.logAssert(this.inputs?.customerId, 'customerId is not defined');
            this.customerId = (this.inputs?.customerId as string) || '0';
        }

        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            this.elementLoaded.emit(false);
            return;
        }

        this.appFacadeService.setLoading(true);
        this.subscriptions.push(this.trackingService.getContractTrackingDetails(this.customerId, this.inputs?.googleMapsGeocodeApiUrl, this.inputs?.googleMapsApiKey));
        this.subscriptions.push(
            this.loaded$.subscribe(loaded => {
                if (loaded) {
                    this.generatePushpins();
                }
            })
        );

        // ADO-114494: Account.UseHapnGps is read once at construction, but the account permission map
        // loads asynchronously and Account.* gates now fail closed. Re-evaluate when the map resolves and
        // wire up the live-link flow then, otherwise live tracking latches disabled for a permitted user.
        this.subscriptions.push(
            this.appFacadeService.isAccountFeaturesLoaded().subscribe(loaded => {
                if (!loaded) {
                    return;
                }
                this.isLiveTrackingFeatureEnabled = this.featureToggleService.isAvailable('liveTracking') && this.featureToggleService.isAvailable('Account.UseHapnGps');
                this.setupLiveLink();
                this.cdRef.markForCheck();
            })
        );

        this.subscriptions.push(
            this.mapReady.subscribe(loaded => {
                if (loaded) {
                    this.subscriptions.push(
                        // Wait 2 seconds after pushpins are generated to hide loader
                        timer(2000).subscribe(() => {
                            this.appFacadeService.setLoading(false);
                        })
                    );
                }
            })
        );

        this.elementLoaded.emit(true);
    }

    // ADO-114494: idempotent — guarded so the live-link flow is wired at most once whether the account
    // map was already resolved at init or resolves later via isAccountFeaturesLoaded.
    private setupLiveLink(): void {
        if (this.liveLinkInitialized || !this.isLiveTrackingFeatureEnabled || !this.customerId) {
            return;
        }
        this.liveLinkInitialized = true;

        this.appFacadeService.loadLiveLink(this.customerId);

        // Subscribe to live link loading state
        this.subscriptions.push(
            this.appFacadeService.isLiveLinkLoading().subscribe(loading => {
                this.isLiveLinkLoading.set(loading);
            })
        );

        // Subscribe to live link URL and store it
        this.subscriptions.push(
            this.appFacadeService.getLiveLinkUrl().subscribe(url => {
                if (url) {
                    this.liveLinkUrl.set(url);
                }
            })
        );
    }

    checkContracts(contracts: TrackingResponse[]) {
        if (contracts?.length > 0) {
            // Check if any contract has lat and long
            const orderHasAddress = contracts?.some(contract => !isEmpty(contract?.lat) && !isEmpty(contract?.long)) ?? false;
            this.orderExistsWithNoAddress$.next(!orderHasAddress);
        }

        if (contracts?.length === 0) {
            // Display correct error
            this.noTrackingOrders$.next(true);
        }

        this.mapReady.next(true);
    }

    generatePushpins() {
        if (this.customerId) {
            this.subscriptions.push(
                this.trackingService.trackingDetails.subscribe((contracts: TrackingResponse[]) => {
                    let fetchedLatLong: Array<boolean | Observable<object> | null> = [];

                    contracts.map((contract, index) => {
                        if (contract?.latLongResult) {
                            fetchedLatLong[index] = contract?.latLongResult;
                        }
                    });
                    fetchedLatLong = fetchedLatLong.filter(ob => ob);

                    // There are no addresses to fetch lat long from, so check contracts to display message
                    if (!fetchedLatLong || fetchedLatLong.length === 0) {
                        this.checkContracts(contracts);
                        return;
                    }

                    // Resolve each observable and get the lat long
                    this.subscriptions.push(
                        combineLatest(filter(fetchedLatLong, size))
                            .pipe(
                                tap({
                                    error: () => {
                                        this.mapReady.next(false);
                                    },
                                    next: (res: unknown) => {
                                        const resResponse: GoogleMapResult[] = res as GoogleMapResult[];
                                        contracts = contracts.map((contract: TrackingResponse, index: number) => {
                                            if (contract?.latLongResult) {
                                                return {
                                                    ...contract,
                                                    lat: resResponse?.[index]?.results?.[0]?.geometry?.location?.lat.toString(),
                                                    long: resResponse?.[index]?.results?.[0]?.geometry?.location?.lng.toString()
                                                };
                                            }
                                            return contract;
                                        });
                                        contracts = this.trackingService.handleDuplicates(contracts);
                                        contracts.map(contract => {
                                            if (contract?.lat && contract?.long) {
                                                if (this.pushpins.length <= contracts.length - 1) {
                                                    this.pushpins.push({
                                                        lat: Number(contract?.lat),
                                                        long: Number(contract?.long),
                                                        title: contract?.itemName ?? '',
                                                        description: contract?.address ?? ''
                                                    });
                                                }
                                            }
                                        });
                                    }
                                })
                            )
                            // After all resolved, call check contracts to display correct message or map
                            .pipe(
                                tap(() => {
                                    this.checkContracts(contracts);
                                })
                            )
                            .subscribe()
                    );
                })
            );
        }
    }

    /**
     * Open live link for the customer
     */
    openLivelink() {
        if (this.isLiveLinkLoading() || !this.liveLinkUrl()) {
            return;
        }
        // If URL is available, open it
        if (this.liveLinkUrl()) {
            window.open(this.liveLinkUrl() ?? '', '_blank', 'noopener,noreferrer');
            return;
        }
    }
}
